"use client"

import { useTheme } from "next-themes"
import { Toaster as Sonner, type ToasterProps, toast } from "sonner"

/**
 * Toast host (shadcn `sonner`). Styled via CSS vars so it inherits each
 * tenant's resolved theme (`@workspace/theming`), and follows the active
 * light/dark theme through `next-themes` (falls back to "system" when no
 * ThemeProvider is mounted). Mount ONCE per app, high in the tree.
 *
 * Only the handful of knobs sonner exposes as CSS vars live here — surface,
 * text, border, corner radius, width. Everything else sonner hard-codes
 * (font, shadow, buttons, state icons) is re-themed in the
 * `[data-sonner-toaster]` block in `../styles/globals.css`, which also carries
 * the dark-mode overrides that can't be expressed in an inline style.
 */
function Toaster({ ...props }: ToasterProps) {
  const { theme = "system" } = useTheme()

  return (
    <Sonner
      theme={theme as ToasterProps["theme"]}
      className="toaster group"
      style={
        {
          "--normal-bg": "var(--popover)",
          "--normal-text": "var(--popover-foreground)",
          "--normal-border": "var(--border)",
          // Close-button hover (sonner only reads these in its dark block).
          "--normal-bg-hover": "var(--muted)",
          "--normal-border-hover": "var(--border)",
          // Sonner's neutral ramp. `--gray11` colors the `toast.loading`
          // spinner bars, which are painted by a plain class rule (not
          // `currentColor`), so it can't be reached from the CSS block —
          // inline wins over sonner's own `[data-theme='dark']` ramp too.
          "--gray11": "var(--muted-foreground)",
          // Tenant corner radius (same token the cards/dialogs behind the
          // toast use) instead of sonner's fixed 8px.
          "--border-radius": "var(--radius)",
          "--width": "22rem",
        } as React.CSSProperties
      }
      {...props}
    />
  )
}

export { Toaster, toast }
